/* eslint-disable prefer-rest-params */
import { WorkspacePreset } from '../../store/workspaces/types';
import commonSearchQueryBuilder from '../search/commonSearchQueryBuilder';

import RequestService from './RequestService';
import { SignedWebsocketUrlResponse, WorkspaceLoadingService } from './types';
import { apiRoots } from './apiConstants';
import { commonEventsQueryBuilder, EventLogsQuery } from '../commonEventsQueryBuilder';
import { PrintEventPayload } from '../../store/workspaces/actions';

let requestService: RequestService;
let workspaceLoadingService: WorkspaceLoadingService;

export const configureCommonApiService = (reqService: RequestService, wspLoadingService: WorkspaceLoadingService): void => {
    requestService = reqService;
    workspaceLoadingService = wspLoadingService;
};

const commonApiService = {
    async fetchWorkspace(workspaceId: string): Promise<WorkspacePreset> {
        return workspaceLoadingService.fetchWorkspace(workspaceId);
    },

    async fetchSignedWebSocketUrl(): Promise<SignedWebsocketUrlResponse> {
        const url = `${apiRoots.ASSESSMENT}GetSignedWebSocketUrl`;
        return requestService.executeRequest(
            requestService.createFetchRequestObject(url),
        );
    },

    async fetchLoggedInUser(): Promise<any> {
        const url = `${apiRoots.ASSESSMENT}DescribeLoggedInUser`;
        return requestService.executeRequest(
            requestService.createFetchRequestObject(url),
        );
    },

    async fetchMarketplaceProperty(property: string, signal?: AbortSignal) {
        const url = `/${apiRoots.KERNEL}GetMarketplaceProperty?propertyName=${property}`;
        return requestService.executeRequest(
            requestService.createFetchRequestObject(url, signal)
        );
    },

    async logout() {
        const url = `${apiRoots.KERNEL}json/UserLogout`;
        const postData: object = {
            languageAgnostic: true,
        };

        return requestService.executeRequest(
            requestService.createFormDataPostRequestObject(url, postData)
        );
    },

    async fetchMembershipOrganizationTree(signal?: AbortSignal) {
        const url = `${apiRoots.ASSESSMENT}GetMembershipOrganizationTree`;
        return requestService.executeRequest(
            requestService.createFetchRequestObject(url, signal)
        );
    },

    async fetchListLiveData(type: string, searchJSON: string, signal?: AbortSignal) {
        const cleanJSON = commonSearchQueryBuilder.cleanQuery(searchJSON);
        let url = `${apiRoots.ASSESSMENT}ListLiveData?type=${type}`;
        url = `${url}&searchJSON=${cleanJSON}`;
        return requestService.executeRequest(
            requestService.createFetchRequestObject(url, signal),
        );
    },

    async getPDFPresignedURLFromFileServletUrl(url: string): Promise<any> {
        return requestService.executeRequest(
            requestService.createFetchRequestObject(url)
        );
    },

    async fetchMultiListLiveData(type: string, searchJSONs: string[], signal?: AbortSignal) {
        const multiQuery = commonSearchQueryBuilder.encodeQueriesForMultiSearch(searchJSONs);
        let url = `${apiRoots.ASSESSMENT}ListLiveData?type=${type}`;
        url = `${url}&searchJSON=${multiQuery}`;
        return requestService.executeRequest(
            requestService.createFetchRequestObject(url, signal)
        );
    },

    async fetchEventLogs(searchJSON: EventLogsQuery, signal?: AbortSignal) {
        const cleanJSON = commonEventsQueryBuilder.cleanQuery(searchJSON);
        let url = `${apiRoots.ASSESSMENT}GetEvents`;
        url = `${url}?searchJSON=${cleanJSON}&timezoneOffsetMinutes=120`;
        return requestService.executeRequest(
            requestService.createFetchRequestObject(url, signal),
        );
    },

    async fetchCustomFieldDefinitions(contentType: string, itemBankId?: number, signal?: AbortSignal) {
        let url = `${apiRoots.ASSESSMENT}GetObjectCustomFieldDefinitions?contentType=${contentType}`;
        if (itemBankId) {
            url = `${url}&itemBankId=${itemBankId}&isActive=true`;
        }
        return requestService.executeRequest(
            requestService.createFetchRequestObject(url, signal)
        );
    },

    async getUploaderInfo() {
        const url = `${apiRoots.ASSESSMENT}GetUploaderInfo`;
        return requestService.executeRequest(
            requestService.createFetchRequestObject(url)
        );
    },

    async createPrintEventMessage({ contextObjectId, printStyle = 'standard' }: PrintEventPayload) {
        const url = `${apiRoots.ASSESSMENT}assessment/CreatePrintEventMessage?`
            + `contextObjectId=${contextObjectId}&printStyleType=${printStyle}&action=print`;
        return requestService.executeRequest(requestService.createFetchRequestObject(url));
    },

    async updateUserRole(
        payloadData: {
            contentItemId: number;
            customUserRoleId: number;
            revokeContributorRole: boolean;
            userId: number;
            notify: boolean;
            triggerEvent?: boolean;
        }
    ) {
        const url = `${apiRoots.CONTENT}AddOrRemoveContentContributorWithCustomUserRole`;
        return requestService.executeRequest(
            requestService.createFormDataPostRequestObject(url, payloadData)
        );
    },

    async getCalibrationData(url: string) {
        return requestService.executeRequest(requestService.createFetchRequestObject(url));
    },

    async bulkUpdateUserRoles(payload: {
        updates: {
            contentItemId: number;
            customUserRoleId: number;
            revokeContributorRole: boolean;
            userId: number;
            notify: boolean;
            triggerEvent: boolean;
        }[];
    } | {
        userId: number;
        notify: boolean;
        triggerEvent: boolean;
        operations: {
            customUserRoleId: number;
            assignContentItemIds: number[];
            revokeContentItemIds: number[];
        }[];
    }) {
        const url = `${apiRoots.CONTENT}BulkUpdateContentContributorsWithCustomUserRole`;
        return requestService.executeRequest(
            requestService.createJsonPostRequestWithTokenObject(url, payload)
        );
    },

    async checkUserCustomRoleSFX(url: string) {
        return requestService.executeRequest(requestService.createFetchRequestObject(url));
    },
};

export default commonApiService;
